import { FC, useContext, useEffect, useRef } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { useCountTo } from "../../hooks/useCountTo";
import { useI18n } from "../../hooks/useTranslations";
import { useCarLeasingApi } from "../../http/carLeasings/api";
import { PaginationQuery } from "../../types";
import { formatNumber } from "../../types/utils";
import styles from "../cars/CarStats.module.css";

type CarLeasingStatsProps = {
  query: PaginationQuery;
};

const CarLeasingStats: FC<CarLeasingStatsProps> = ({ query }) => {
  const i18n = useI18n();
  const carLeasingApi = useCarLeasingApi();
  const aborter = useRef(new AbortController());
  const dispatch = useContext(NotificationDispatch);

  const statsResult = carLeasingApi.useCarLeasingStats(query, aborter.current.signal);

  useEffect(() => {
    statsResult.error && dispatch(showErrorNotification(i18n, statsResult.error));
  }, [statsResult.error]);

  const stats = [
    {
      number: statsResult.data?.totalActive,
      title: i18n.t.common.active,
      color: "#b3ffc2",
      borderColor: "#4aa55c80",
    },
    {
      number: statsResult.data?.totalExpiringSoon,
      title: i18n.t.common.expiringSoon,
      color: "#ffffad",
      borderColor: "#9b9b5280",
    },
    {
      number: statsResult.data?.totalExpired,
      title: i18n.t.common.expired,
      color: "#fa8383ff",
      borderColor: "#93474750",
    },
  ];

  return (
    <div className={styles["stats-container"]}>
      {stats.map((stat) => (
        <StatCard
          key={stat.title}
          number={stat.number}
          title={stat.title}
          color={stat.color}
          borderColor={stat.borderColor}
          isLoading={!statsResult.data}
          language={i18n.language}
        />
      ))}
    </div>
  );
};

export default CarLeasingStats;

type StatCardProps = {
  number?: number;
  title: string;
  color: string;
  borderColor: string;
  isLoading: boolean;
  language: string;
};

const StatCard: FC<StatCardProps> = ({ number, title, color, borderColor, isLoading, language }) => {
  const animatedNumber = useCountTo({ target: number, isReady: !isLoading });

  return (
    <div className={styles["stat-card"]} style={{ backgroundColor: color, borderColor }}>
      <div className={styles["number-container"]}>
        <div className={styles["stat-number"]}>{formatNumber(animatedNumber, language, { fractionalDigits: 0 })}</div>
      </div>
      <div className={styles["stat-title"]}>{title}</div>
    </div>
  );
};
